import React from 'react';
import Axios from 'axios';
import './DropsPage.scss';
import { localeURI } from '../../utils/util';

class DropsPage extends React.Component {
  constructor(props) {
    super(props);
    this.props = props;
    this.state = {
      isLoading: true,
      isFulfilling: false,
      isDebugLoading: false,
      isRewardConfigLoading: false,
      isSteamworksTestLoading: false,
      isSteamworksGrantLoading: false,
      error: null,
      overview: null,
      lastRefreshedAt: null,
      debugError: null,
      debugData: null,
      rewardConfigError: null,
      rewardConfigData: null,
      steamworksTestError: null,
      steamworksTestData: null,
      steamworksTestAppId: '',
      steamworksGrantError: null,
      steamworksGrantData: null,
      steamworksGrantBenefitId: '',
      steamworksGrantSteamId: '',
      steamworksGrantType: '',
      steamworksGrantPackageId: '',
      steamworksGrantAppId: '',
      steamworksGrantItemDefId: '',
      steamworksGrantQuantity: '1',
      debugGameName: '',
      debugGameId: '',
      debugBroadcasterId: '',
      debugCampaignIds: '',
    };
  }

  componentDidMount() {
    this.refresh();
  }

  getAuthHeaders = () => {
    const token = this.props?.auth?.token;
    return { headers: { Authorization: `JWT ${token}` } };
  };

  connectTwitch = () => {
    try {
      localStorage.setItem('previousPath', '/drops');
    } catch (e) {
      // ignore localStorage errors
    }
    window.location.href = localeURI('/api/v1/auth/twitch');
  };

  linkDropsAccount = () => {
    window.location.href = '/drops/link/start';
  };

  refresh = async () => {
    try {
      this.setState({ isLoading: true, error: null });
      const debug = typeof window !== 'undefined' && String(new URL(window.location.href).searchParams.get('debug') || '') === '1';
      const previousSummary = this.state?.overview?.summary || null;
      const resp = await Axios.get('/api/v1/user/drops/overview', {
        ...this.getAuthHeaders(),
        ...(debug ? { params: { debug: 1 } } : {}),
      });
      const overview = resp?.data?.data || null;
      this.setState({ overview, isLoading: false, lastRefreshedAt: new Date().toISOString() });

      if (debug) {
        // eslint-disable-next-line no-console
        console.log('[DropsPage] refresh', {
          at: new Date().toISOString(),
          previousSummary,
          summary: overview?.summary || null,
          sync: overview?.sync || null,
        });
      }
    } catch (e) {
      this.setState({
        isLoading: false,
        error: e?.response?.data?.message || e.message || 'Failed to load Drops overview',
      });
    }
  };

  fulfill = async () => {
    try {
      this.setState({ isFulfilling: true, error: null });
      await Axios.post('/api/v1/user/drops/fulfill', {}, this.getAuthHeaders());
      await this.refresh();
    } catch (e) {
      this.setState({
        isFulfilling: false,
        error: e?.response?.data?.message || e.message || 'Failed to fulfill entitlements',
      });
    } finally {
      this.setState({ isFulfilling: false });
    }
  };

  runDebug = async () => {
    try {
      const { debugGameName, debugGameId, debugBroadcasterId, debugCampaignIds } = this.state;
      this.setState({ isDebugLoading: true, debugError: null });

      const params = {};
      if (debugGameName && debugGameName.trim()) params.gameName = debugGameName.trim();
      if (debugGameId && debugGameId.trim()) params.gameId = debugGameId.trim();
      if (debugBroadcasterId && debugBroadcasterId.trim()) params.broadcasterId = debugBroadcasterId.trim();
      if (debugCampaignIds && debugCampaignIds.trim()) params.campaignIds = debugCampaignIds.trim();

      const resp = await Axios.get('/api/v1/user/drops/debug', {
        ...this.getAuthHeaders(),
        params,
      });
      this.setState({ debugData: resp?.data || null, isDebugLoading: false });
    } catch (e) {
      this.setState({
        isDebugLoading: false,
        debugError: e?.response?.data?.message || e.message || 'Failed to run Drops debug',
      });
    }
  };

  loadRewardConfig = async () => {
    try {
      this.setState({ isRewardConfigLoading: true, rewardConfigError: null });
      const resp = await Axios.get('/api/v1/user/drops/rewards/config', this.getAuthHeaders());
      this.setState({ rewardConfigData: resp?.data || null, isRewardConfigLoading: false });
    } catch (e) {
      this.setState({
        isRewardConfigLoading: false,
        rewardConfigError: e?.response?.data?.message || e.message || 'Failed to load Drops reward config',
      });
    }
  };

  testSteamworksKey = async () => {
    try {
      this.setState({ isSteamworksTestLoading: true, steamworksTestError: null });
      const appId = (this.state?.steamworksTestAppId || '').trim();
      const resp = await Axios.get('/api/v1/user/drops/steamworks/validate', {
        ...this.getAuthHeaders(),
        ...(appId ? { params: { appId } } : {}),
      });
      this.setState({ steamworksTestData: resp?.data || null, isSteamworksTestLoading: false });
    } catch (e) {
      this.setState({
        isSteamworksTestLoading: false,
        steamworksTestError: e?.response?.data?.message || e.message || 'Steamworks key test failed',
      });
    }
  };

  isAdmin = () => {
    return !!this.props?.auth?.user?.permission;
  };

  applyRewardConfigToSteamworksTest = (benefitId) => {
    try {
      const rewards = this.state?.rewardConfigData?.data?.rewards || [];
      const id = (benefitId || '').toString().trim();
      const cfg = rewards.find((r) => String(r?.benefitId || '').trim() === id) || null;
      if (!cfg) return;

      const type = (cfg?.steamPackageId ? 'PACKAGE' : (cfg?.steamItemDefId ? 'INVENTORY' : '')).toString();
      this.setState({
        steamworksGrantBenefitId: id,
        steamworksGrantType: type,
        steamworksGrantPackageId: cfg?.steamPackageId ? String(cfg.steamPackageId) : '',
        steamworksGrantAppId: cfg?.steamAppId ? String(cfg.steamAppId) : '',
        steamworksGrantItemDefId: cfg?.steamItemDefId ? String(cfg.steamItemDefId) : '',
        steamworksGrantQuantity: cfg?.quantity !== undefined && cfg?.quantity !== null ? String(cfg.quantity) : '1',
      });
    } catch (e) {
      // ignore
    }
  };

  testSteamworksGrant = async () => {
    try {
      const {
        steamworksGrantBenefitId,
        steamworksGrantSteamId,
        steamworksGrantType,
        steamworksGrantPackageId,
        steamworksGrantAppId,
        steamworksGrantItemDefId,
        steamworksGrantQuantity,
      } = this.state;

      this.setState({ isSteamworksGrantLoading: true, steamworksGrantError: null, steamworksGrantData: null });

      const payload = {};
      if (steamworksGrantBenefitId && steamworksGrantBenefitId.trim()) payload.benefitId = steamworksGrantBenefitId.trim();
      if (steamworksGrantSteamId && steamworksGrantSteamId.trim()) payload.steamId = steamworksGrantSteamId.trim();
      if (steamworksGrantType && steamworksGrantType.trim()) payload.type = steamworksGrantType.trim();
      if (steamworksGrantPackageId && steamworksGrantPackageId.trim()) payload.packageId = steamworksGrantPackageId.trim();
      if (steamworksGrantAppId && steamworksGrantAppId.trim()) payload.appId = steamworksGrantAppId.trim();
      if (steamworksGrantItemDefId && steamworksGrantItemDefId.trim()) payload.itemDefId = steamworksGrantItemDefId.trim();
      if (steamworksGrantQuantity && String(steamworksGrantQuantity).trim()) payload.quantity = Number(String(steamworksGrantQuantity).trim());

      const resp = await Axios.post('/api/v1/user/drops/steamworks/reward/test', payload, this.getAuthHeaders());
      this.setState({ steamworksGrantData: resp?.data || null, isSteamworksGrantLoading: false });
    } catch (e) {
      const respData = e?.response?.data || null;
      this.setState({
        isSteamworksGrantLoading: false,
        steamworksGrantError: (respData && (respData.message || respData.code)) ? (respData.message || respData.code) : (e.message || 'Steamworks grant test failed'),
        steamworksGrantData: respData,
      });
    }
  };

  formatIso = (value) => {
    try {
      if (!value) return '-';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString();
    } catch (e) {
      return String(value || '-');
    }
  };

  normalizeEntitlementStatus = (status) => {
    const s = (status || '').toString().trim().toUpperCase();
    if (!s) return 'UNKNOWN';
    return s;
  };

  truncateMiddle = (value, head = 12, tail = 10) => {
    const s = (value || '').toString();
    if (!s) return '';
    if (s.length <= head + tail + 3) return s;
    return `${s.slice(0, head)}…${s.slice(-tail)}`;
  };

  copyToClipboard = async (text) => {
    const value = (text || '').toString();
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return;
      }
    } catch (e) {
      // ignore
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (e) {
      // ignore
    }
  };

  getStatusLabel = (status) => {
    const s = this.normalizeEntitlementStatus(status);
    if (s === 'FULFILLED') return 'FULFILLED (已发放)';
    if (s === 'CLAIMED') return 'CLAIMED (已领取)';
    return s;
  };

  getStatusClass = (status) => {
    const s = this.normalizeEntitlementStatus(status);
    if (s === 'FULFILLED') return 'DropsPage__statusPill DropsPage__statusPill--fulfilled';
    if (s === 'CLAIMED') return 'DropsPage__statusPill DropsPage__statusPill--claimed';
    return 'DropsPage__statusPill DropsPage__statusPill--unknown';
  };

  buildEntitlementIndex = (entitlements) => {
    const index = {};
    (entitlements || []).forEach((e) => {
      const benefitId = e?.benefitId ? String(e.benefitId) : '';
      if (!benefitId) return;

      const current = index[benefitId];
      const eTime = new Date(e?.lastUpdatedAt || e?.grantedAt || e?.createdAt || 0).getTime();
      const cTime = current ? new Date(current?.lastUpdatedAt || current?.grantedAt || current?.createdAt || 0).getTime() : -1;
      if (!current || eTime >= cTime) index[benefitId] = e;
    });
    return index;
  };

  renderRewards = (overview) => {
    const campaigns = Array.isArray(overview?.campaigns) ? overview.campaigns : [];
    const entitlements = Array.isArray(overview?.entitlements) ? overview.entitlements : [];
    const entByBenefit = this.buildEntitlementIndex(entitlements);

    const get = (obj, ...keys) => {
      for (const k of keys) {
        if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
      }
      return undefined;
    };

    const hasCampaignDrops = campaigns.some((c) => {
      const timeDrops = get(c, 'time_based_drops', 'timeBasedDrops') || [];
      const eventDrops = get(c, 'event_based_drops', 'eventBasedDrops') || [];
      return (Array.isArray(timeDrops) && timeDrops.length) || (Array.isArray(eventDrops) && eventDrops.length);
    });

    return (
      <section className="DropsPage__card">
        <h2>Rewards (我的奖励)</h2>
        <div className="DropsPage__hint">
          <div>
            <span className="DropsPage__statusPill DropsPage__statusPill--claimed">CLAIMED</span> means you claimed it on Twitch.
          </div>
          <div>
            <span className="DropsPage__statusPill DropsPage__statusPill--fulfilled">FULFILLED</span> means Noiz has delivered the reward.
          </div>
        </div>

        {!hasCampaignDrops ? (
          <>
            <div className="DropsPage__empty">
              Campaign reward details are not available (campaigns endpoint may be restricted). Showing your entitlements as reward cards.
            </div>
            {entitlements.length ? (
              <div className="DropsPage__rewardGrid">
                {entitlements.map((e) => (
                  <div key={e.entitlementId} className="DropsPage__rewardCard">
                    <div className="DropsPage__rewardTop">
                      <div className="DropsPage__rewardName">Reward</div>
                      <div className={this.getStatusClass(e.fulfillmentStatus)}>{this.getStatusLabel(e.fulfillmentStatus)}</div>
                    </div>
                    <div className="DropsPage__rewardMeta">
                      Benefit: <span className="mono">{e.benefitId || '-'}</span>
                    </div>
                    <div className="DropsPage__rewardMeta">
                      Granted: <span className="mono">{e.grantedAt ? this.formatIso(e.grantedAt) : '-'}</span>
                    </div>
                    <div className="DropsPage__rewardIds">
                      Entitlement:{' '}
                      <span className="mono mono--truncate" title={e.entitlementId || ''}>
                        {this.truncateMiddle(e.entitlementId, 14, 12)}
                      </span>
                      <button
                        type="button"
                        className="DropsPage__copyBtn"
                        onClick={() => this.copyToClipboard(e.entitlementId)}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </>
        ) : (
          <div className="DropsPage__campaignGrid">
            {campaigns.map((c) => {
              const campaignId = get(c, 'id');
              const campaignName = get(c, 'name') || 'Campaign';
              const campaignStatus = get(c, 'status') || '-';
              const campaignStart = get(c, 'start_time', 'startTime');
              const campaignEnd = get(c, 'end_time', 'endTime');

              const timeDrops = get(c, 'time_based_drops', 'timeBasedDrops') || [];
              const eventDrops = get(c, 'event_based_drops', 'eventBasedDrops') || [];
              const allDrops = []
                .concat(Array.isArray(timeDrops) ? timeDrops.map((d) => ({ type: 'TIME', d })) : [])
                .concat(Array.isArray(eventDrops) ? eventDrops.map((d) => ({ type: 'EVENT', d })) : []);

              if (!allDrops.length) return null;

              return (
                <div key={campaignId || campaignName} className="DropsPage__campaignCard">
                  <div className="DropsPage__campaignHeader">
                    <div className="DropsPage__campaignTitle">{campaignName}</div>
                    <div className="DropsPage__campaignMeta">
                      <span className="mono">{campaignStatus}</span> · {this.formatIso(campaignStart)} → {this.formatIso(campaignEnd)}
                    </div>
                  </div>

                  <div className="DropsPage__rewardGrid">
                    {allDrops.map(({ type, d }) => {
                      const dropId = get(d, 'id');
                      const dropName = get(d, 'name') || 'Drop';
                      const benefitId = get(d, 'benefit_id', 'benefitId') ? String(get(d, 'benefit_id', 'benefitId')) : '';
                      const requiredMinutes = get(d, 'required_minutes_watched', 'requiredMinutesWatched');
                      const dropStart = get(d, 'start_time', 'startTime');
                      const dropEnd = get(d, 'end_time', 'endTime');

                      const ent = benefitId ? entByBenefit[benefitId] : null;
                      const entStatus = ent ? this.getStatusLabel(ent.fulfillmentStatus) : 'NOT CLAIMED (未领取)';
                      const pillClass = ent ? this.getStatusClass(ent.fulfillmentStatus) : 'DropsPage__statusPill DropsPage__statusPill--missing';

                      return (
                        <div key={dropId || `${type}-${benefitId}-${dropName}`} className="DropsPage__rewardCard">
                          <div className="DropsPage__rewardTop">
                            <div className="DropsPage__rewardName">
                              <span className="DropsPage__dropType">{type}</span> {dropName}
                            </div>
                            <div className={pillClass}>{entStatus}</div>
                          </div>

                          <div className="DropsPage__rewardMeta">
                            {requiredMinutes !== undefined && requiredMinutes !== null ? (
                              <span>Watch: <span className="mono">{requiredMinutes} min</span></span>
                            ) : null}
                            {dropStart || dropEnd ? (
                              <span> · Window: <span className="mono">{this.formatIso(dropStart)} → {this.formatIso(dropEnd)}</span></span>
                            ) : null}
                          </div>

                          <div className="DropsPage__rewardIds">
                            {benefitId ? <span>Benefit: <span className="mono">{benefitId}</span></span> : null}
                            {ent?.entitlementId ? (
                              <>
                                {' '}· Entitlement:{' '}
                                <span className="mono mono--truncate" title={ent.entitlementId || ''}>
                                  {this.truncateMiddle(ent.entitlementId, 14, 12)}
                                </span>
                                <button
                                  type="button"
                                  className="DropsPage__copyBtn"
                                  onClick={() => this.copyToClipboard(ent.entitlementId)}
                                >
                                  Copy
                                </button>
                              </>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  renderCampaignInfo = (overview) => {
    const ui = overview?.ui || {};
    const stats = overview?.stats || null;
    const name = ui.campaignName || 'Twitch Drops Campaign';
    const gameName = ui.gameName || '—';
    const startAt = ui.startAt ? this.formatIso(ui.startAt) : '—';
    const endAt = ui.endAt ? this.formatIso(ui.endAt) : '—';

    const boxArt = ui.gameBoxArt
      ? String(ui.gameBoxArt).replace('{width}', '144').replace('{height}', '192')
      : null;

    return (
      <section className="DropsPage__card">
        <h2>Campaign (活动信息)</h2>
        <div className="DropsPage__campaignInfo">
          {boxArt ? (
            <img className="DropsPage__campaignBoxArt" src={boxArt} alt="" />
          ) : null}
          <div className="DropsPage__campaignInfoText">
            <div className="DropsPage__campaignInfoTitle">{name}</div>
            <div className="DropsPage__campaignInfoSub">
              Game: <span className="mono">{gameName}</span>{ui.gameId ? <span> · ID: <span className="mono">{ui.gameId}</span></span> : null}
            </div>
            <div className="DropsPage__campaignInfoSub">
              Window: <span className="mono">{startAt}</span> → <span className="mono">{endAt}</span>
            </div>
            {ui.campaignDescription ? (
              <div className="DropsPage__hint">{ui.campaignDescription}</div>
            ) : null}
            {stats ? (
              <div className="DropsPage__campaignStats">
                <div className="DropsPage__statItem">
                  <div className="DropsPage__statLabel">Participants</div>
                  <div className="DropsPage__statValue">{stats.participants}</div>
                </div>
                <div className="DropsPage__statItem">
                  <div className="DropsPage__statLabel">Claimed</div>
                  <div className="DropsPage__statValue">{stats.usersClaimed}</div>
                </div>
                <div className="DropsPage__statItem">
                  <div className="DropsPage__statLabel">Fulfilled</div>
                  <div className="DropsPage__statValue">{stats.usersFulfilled}</div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    );
  };

  render() {
    const {
      isLoading,
      isFulfilling,
      isDebugLoading,
      isRewardConfigLoading,
      isSteamworksTestLoading,
      isSteamworksGrantLoading,
      error,
      overview,
      lastRefreshedAt,
      debugError,
      debugData,
      rewardConfigError,
      rewardConfigData,
      steamworksTestError,
      steamworksTestData,
      steamworksTestAppId,
      steamworksGrantError,
      steamworksGrantData,
      steamworksGrantBenefitId,
      steamworksGrantSteamId,
      steamworksGrantType,
      steamworksGrantPackageId,
      steamworksGrantAppId,
      steamworksGrantItemDefId,
      steamworksGrantQuantity,
      debugGameName,
      debugGameId,
      debugBroadcasterId,
      debugCampaignIds,
    } = this.state;

    return (
      <section className="DropsPage">
        <header className="DropsPage__header">
          <h1>Twitch Drops</h1>
          <div className="DropsPage__actions">
            <button type="button" className="site-button" onClick={this.refresh} disabled={isLoading}>
              Refresh
            </button>
            {!overview?.isTwitchLinked ? (
              <button type="button" className="site-button" onClick={this.connectTwitch} disabled={isLoading}>
                Link Twitch
              </button>
            ) : null}
            <button
              type="button"
              className="site-button"
              onClick={this.fulfill}
              disabled={isLoading || isFulfilling || !overview?.enabled || !overview?.isTwitchLinked}
            >
              {isFulfilling ? 'Fulfilling…' : 'Fulfill Claimed Drops'}
            </button>
          </div>
        </header>

        {lastRefreshedAt ? (
          <div className="DropsPage__hint">Last refreshed: {this.formatIso(lastRefreshedAt)}</div>
        ) : null}

        {error ? <div className="DropsPage__error">{error}</div> : null}
        {isLoading ? <div className="DropsPage__loading">Loading…</div> : null}

        {overview ? (
          <>
            <section className="DropsPage__card">
              <div className="DropsPage__row">
                <div className="DropsPage__label">Enabled</div>
                <div className="DropsPage__value">{overview.enabled ? 'Yes' : 'No'}</div>
              </div>
              <div className="DropsPage__row">
                <div className="DropsPage__label">Twitch Linked</div>
                <div className="DropsPage__value">
                  {overview.isTwitchLinked ? (
                    <span>
                      {overview.twitch?.displayName || overview.twitch?.username} (id: {overview.twitch?.id})
                    </span>
                  ) : (
                    <button type="button" className="site-button" onClick={this.connectTwitch}>
                      Connect Twitch
                    </button>
                  )}
                </div>
              </div>
              <div className="DropsPage__row">
                <div className="DropsPage__label">Drops Account Linked</div>
                <div className="DropsPage__value">
                  {overview.isDropsLinked ? (
                    <span>Yes</span>
                  ) : (
                    <button
                      type="button"
                      className="site-button"
                      onClick={this.linkDropsAccount}
                    >
                      Link for Drops
                    </button>
                  )}
                </div>
              </div>
              <div className="DropsPage__row">
                <div className="DropsPage__label">Inventory</div>
                <div className="DropsPage__value">
                  <a href={overview.config?.inventoryUrl || 'https://www.twitch.tv/drops/inventory'} target="_blank" rel="noreferrer">
                    Open Twitch Drops Inventory
                  </a>
                </div>
              </div>
              <div className="DropsPage__row">
                <div className="DropsPage__label">Summary</div>
                <div className="DropsPage__value">
                  total: {overview.summary?.total || 0}, fulfilled: {overview.summary?.fulfilled || 0}, claimed:{' '}
                  {overview.summary?.claimed || 0}
                </div>
              </div>
              {!overview.enabled ? (
                <div className="DropsPage__hint">
                  Drops is disabled on the server. Ask an admin to set `TWITCH_DROPS_ENABLED=true` and configure the game/campaign.
                </div>
              ) : null}
            </section>

            {this.renderCampaignInfo(overview)}
            {this.renderRewards(overview)}

            <section className="DropsPage__card">
              <h2>Debug</h2>
              <div className="DropsPage__hint">
                Use this panel to verify: app token validity, Helix game id, Drops campaigns response, and entitlements response — without
                leaving the UI.
              </div>
              <div className="DropsPage__hint">
                Tip: to ignore a configured value, type <span className="mono">none</span> (or <span className="mono">-</span>) in the override field.
              </div>

              <div className="DropsPage__debugForm">
                <label className="DropsPage__debugField">
                  <div className="DropsPage__label">Search gameName</div>
                  <input
                    type="text"
                    value={debugGameName}
                    placeholder="Search Twitch category/game name"
                    onChange={(e) => this.setState({ debugGameName: e.target.value })}
                  />
                </label>

                <label className="DropsPage__debugField">
                  <div className="DropsPage__label">Override gameId</div>
                  <input
                    type="text"
                    value={debugGameId}
                    placeholder={overview?.config?.gameId || 'TWITCH_DROPS_GAME_ID'}
                    onChange={(e) => this.setState({ debugGameId: e.target.value })}
                  />
                </label>

                <label className="DropsPage__debugField">
                  <div className="DropsPage__label">Override broadcasterId</div>
                  <input
                    type="text"
                    value={debugBroadcasterId}
                    placeholder={overview?.config?.broadcasterId || 'TWITCH_DROPS_BROADCASTER_ID'}
                    onChange={(e) => this.setState({ debugBroadcasterId: e.target.value })}
                  />
                </label>

                <label className="DropsPage__debugField">
                  <div className="DropsPage__label">Override campaignIds (comma)</div>
                  <input
                    type="text"
                    value={debugCampaignIds}
                    placeholder={(overview?.config?.campaignIds || []).join(',') || 'TWITCH_DROPS_CAMPAIGN_IDS'}
                    onChange={(e) => this.setState({ debugCampaignIds: e.target.value })}
                  />
                </label>
              </div>

	              <div className="DropsPage__actions">
	                <button type="button" className="site-button" onClick={this.runDebug} disabled={isDebugLoading}>
	                  {isDebugLoading ? 'Running…' : 'Run Debug Checks'}
	                </button>
	                <button type="button" className="site-button" onClick={this.loadRewardConfig} disabled={isRewardConfigLoading}>
	                  {isRewardConfigLoading ? 'Loading…' : 'Load Reward Config'}
	                </button>
	                <label className="DropsPage__debugField" style={{ maxWidth: 220 }}>
	                  <div className="DropsPage__label">Steam AppID (optional)</div>
	                  <input
	                    type="text"
	                    value={steamworksTestAppId}
	                    placeholder="e.g. 480"
	                    onChange={(e) => this.setState({ steamworksTestAppId: e.target.value })}
	                  />
	                </label>
	                <button type="button" className="site-button" onClick={this.testSteamworksKey} disabled={isSteamworksTestLoading}>
	                  {isSteamworksTestLoading ? 'Testing…' : 'Test Steamworks Key'}
	                </button>
	              </div>

	              {debugError ? <div className="DropsPage__error">{debugError}</div> : null}

	              {debugData ? (
	                <pre className="DropsPage__debugOutput">{JSON.stringify(debugData, null, 2)}</pre>
	              ) : (
	                <div className="DropsPage__empty">No debug run yet.</div>
	              )}

	              <details className="DropsPage__details" open={!!rewardConfigData}>
	                <summary className="DropsPage__detailsSummary">Reward delivery config (Steamworks)</summary>

	                {rewardConfigError ? <div className="DropsPage__error">{rewardConfigError}</div> : null}

	                {rewardConfigData ? (
	                  <>
	                    <div className="DropsPage__hint">
	                      Steamworks configured: {rewardConfigData?.data?.steamworksConfigured ? 'Yes' : 'No'}
	                    </div>
	                    {(rewardConfigData?.data?.rewards || []).length ? (
	                      <table className="DropsPage__table">
	                        <thead>
	                          <tr>
	                            <th>Benefit ID</th>
	                            <th>Type</th>
	                            <th>Steam App</th>
	                            <th>ItemDef</th>
	                            <th>Package</th>
	                            <th>Qty</th>
	                          </tr>
	                        </thead>
	                        <tbody>
	                          {rewardConfigData.data.rewards.map((r) => (
	                            <tr key={r.benefitId}>
	                              <td className="mono mono--truncate" title={r.benefitId || ''}>
	                                {this.truncateMiddle(r.benefitId, 16, 12)}
	                              </td>
	                              <td>{r.type || '-'}</td>
	                              <td className="mono">{r.steamAppId || '-'}</td>
	                              <td className="mono">{r.steamItemDefId || '-'}</td>
	                              <td className="mono">{r.steamPackageId || '-'}</td>
	                              <td className="mono">{r.quantity ?? '-'}</td>
	                            </tr>
	                          ))}
	                        </tbody>
	                      </table>
	                    ) : (
	                      <div className="DropsPage__empty">
	                        No reward mappings configured. Set <span className="mono">TWITCH_DROPS_REWARDS_JSON</span> on the backend.
	                      </div>
	                    )}
	                    <details className="DropsPage__details">
	                      <summary className="DropsPage__detailsSummary">Raw reward config response</summary>
	                      <pre className="DropsPage__debugOutput">{JSON.stringify(rewardConfigData, null, 2)}</pre>
	                    </details>
	                  </>
	                ) : (
	                  <div className="DropsPage__empty">
	                    Click “Load Reward Config” to verify <span className="mono">TWITCH_DROPS_REWARDS_JSON</span>.
	                  </div>
	                )}
	              </details>

	              <details className="DropsPage__details" open={!!steamworksTestData || !!steamworksTestError}>
	                <summary className="DropsPage__detailsSummary">Steamworks key validation</summary>
	                {steamworksTestError ? <div className="DropsPage__error">{steamworksTestError}</div> : null}
	                {steamworksTestData ? (
	                  <>
	                    <div className="DropsPage__hint">
	                      Result: {steamworksTestData?.data?.ok ? 'OK' : 'FAILED'} (HTTP {steamworksTestData?.data?.status || '-'}) · Endpoint:{' '}
	                      <span className="mono">{steamworksTestData?.data?.endpoint || '-'}</span>
	                      {steamworksTestData?.data?.appId ? (
	                        <span> · AppID: <span className="mono">{steamworksTestData.data.appId}</span></span>
	                      ) : null}
	                    </div>
	                    {typeof steamworksTestData?.data?.keyOk === 'boolean' ? (
	                      <div className="DropsPage__hint">
	                        Key reachable: {steamworksTestData.data.keyOk ? 'Yes' : 'No'}
	                        {steamworksTestData.data.keyStatus ? <span> (HTTP {steamworksTestData.data.keyStatus})</span> : null}
	                      </div>
	                    ) : null}
	                    {typeof steamworksTestData?.data?.inventoryOk === 'boolean' ? (
	                      <div className="DropsPage__hint">
	                        Inventory permission for AppID: {steamworksTestData.data.inventoryOk ? 'Yes' : 'No'}
	                        {steamworksTestData.data.inventoryStatus ? <span> (HTTP {steamworksTestData.data.inventoryStatus})</span> : null}
	                      </div>
	                    ) : null}
	                    {steamworksTestData?.data?.warning ? (
	                      <div className="DropsPage__hint">{steamworksTestData.data.warning}</div>
	                    ) : null}
	                    {steamworksTestData?.data?.hint ? (
	                      <div className="DropsPage__hint">{steamworksTestData.data.hint}</div>
	                    ) : null}
	                    <details className="DropsPage__details">
	                      <summary className="DropsPage__detailsSummary">Raw validation response</summary>
	                      <pre className="DropsPage__debugOutput">{JSON.stringify(steamworksTestData, null, 2)}</pre>
	                    </details>
	                  </>
	                ) : (
	                  <div className="DropsPage__empty">Click “Test Steamworks Key” to validate `STEAMWORKS_PARTNER_KEY`.</div>
	                )}
	              </details>

	              {this.isAdmin() ? (
	                <details className="DropsPage__details" open={!!steamworksGrantData || !!steamworksGrantError}>
	                  <summary className="DropsPage__detailsSummary">Steamworks reward delivery test (admin)</summary>
	                  <div className="DropsPage__hint">
	                    Grants Twitch Drops rewards via Steamworks Partner Web API (DLC: GrantPackage, Items: Inventory AddItem). Requires{' '}
	                    <span className="mono">STEAMWORKS_TEST_GRANTS_ENABLED=true</span>.
	                  </div>

	                  {steamworksGrantError ? <div className="DropsPage__error">{steamworksGrantError}</div> : null}

	                  <div className="DropsPage__debugForm">
	                    <label className="DropsPage__debugField" style={{ maxWidth: 420 }}>
	                      <div className="DropsPage__label">Benefit ID (optional, from reward config)</div>
	                      <select
	                        value={steamworksGrantBenefitId}
	                        onChange={(e) => {
	                          const id = e.target.value;
	                          this.setState({ steamworksGrantBenefitId: id });
	                          this.applyRewardConfigToSteamworksTest(id);
	                        }}
	                      >
	                        <option value="">(manual)</option>
	                        {(rewardConfigData?.data?.rewards || []).map((r) => (
	                          <option key={r.benefitId} value={r.benefitId}>
	                            {r.type || '-'} · {this.truncateMiddle(r.benefitId, 16, 12)}
	                          </option>
	                        ))}
	                      </select>
	                    </label>

	                    <label className="DropsPage__debugField" style={{ maxWidth: 420 }}>
	                      <div className="DropsPage__label">SteamID (optional override)</div>
	                      <input
	                        type="text"
	                        value={steamworksGrantSteamId}
	                        placeholder="Default: linked SteamID of this admin user"
	                        onChange={(e) => this.setState({ steamworksGrantSteamId: e.target.value })}
	                      />
	                    </label>

	                    <label className="DropsPage__debugField" style={{ maxWidth: 220 }}>
	                      <div className="DropsPage__label">Type</div>
	                      <select value={steamworksGrantType} onChange={(e) => this.setState({ steamworksGrantType: e.target.value })}>
	                        <option value="">(auto)</option>
	                        <option value="PACKAGE">PACKAGE (GrantPackage)</option>
	                        <option value="INVENTORY">INVENTORY (AddItem)</option>
	                      </select>
	                    </label>

	                    <label className="DropsPage__debugField" style={{ maxWidth: 220 }}>
	                      <div className="DropsPage__label">PackageID (DLC)</div>
	                      <input
	                        type="text"
	                        value={steamworksGrantPackageId}
	                        placeholder="e.g. 123456"
	                        onChange={(e) => this.setState({ steamworksGrantPackageId: e.target.value })}
	                      />
	                    </label>

	                    <label className="DropsPage__debugField" style={{ maxWidth: 220 }}>
	                      <div className="DropsPage__label">AppID</div>
	                      <input
	                        type="text"
	                        value={steamworksGrantAppId}
	                        placeholder="e.g. 480"
	                        onChange={(e) => this.setState({ steamworksGrantAppId: e.target.value })}
	                      />
	                    </label>

	                    <label className="DropsPage__debugField" style={{ maxWidth: 220 }}>
	                      <div className="DropsPage__label">ItemDefID</div>
	                      <input
	                        type="text"
	                        value={steamworksGrantItemDefId}
	                        placeholder="e.g. 1001"
	                        onChange={(e) => this.setState({ steamworksGrantItemDefId: e.target.value })}
	                      />
	                    </label>

	                    <label className="DropsPage__debugField" style={{ maxWidth: 160 }}>
	                      <div className="DropsPage__label">Qty</div>
	                      <input
	                        type="number"
	                        min="1"
	                        value={steamworksGrantQuantity}
	                        onChange={(e) => this.setState({ steamworksGrantQuantity: e.target.value })}
	                      />
	                    </label>
	                  </div>

	                  <div className="DropsPage__actions">
	                    <button type="button" className="site-button" onClick={this.testSteamworksGrant} disabled={isSteamworksGrantLoading}>
	                      {isSteamworksGrantLoading ? 'Sending…' : 'Test Deliver via Steamworks'}
	                    </button>
	                  </div>

	                  {steamworksGrantData ? (
	                    <pre className="DropsPage__debugOutput">{JSON.stringify(steamworksGrantData, null, 2)}</pre>
	                  ) : (
	                    <div className="DropsPage__empty">
	                      Pick a Benefit ID (after “Load Reward Config”), or fill fields manually and click “Test Deliver via Steamworks”.
	                    </div>
	                  )}
	                </details>
	              ) : null}

	              <details className="DropsPage__details">
	                <summary className="DropsPage__detailsSummary">Raw entitlements (advanced)</summary>
	                {overview.entitlements && overview.entitlements.length ? (
	                  <table className="DropsPage__table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Benefit</th>
                        <th>Granted</th>
                        <th>Last Updated</th>
                        <th>Entitlement ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.entitlements.map((e) => (
                        <tr key={e.entitlementId}>
                          <td><span className={this.getStatusClass(e.fulfillmentStatus)}>{this.getStatusLabel(e.fulfillmentStatus)}</span></td>
                          <td className="mono">{e.benefitId || '-'}</td>
                          <td>{e.grantedAt ? this.formatIso(e.grantedAt) : '-'}</td>
                          <td>{e.lastUpdatedAt ? this.formatIso(e.lastUpdatedAt) : '-'}</td>
                          <td className="mono mono--truncate" title={e.entitlementId || ''}>{this.truncateMiddle(e.entitlementId, 16, 12)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="DropsPage__empty">
                    No entitlements yet. After watching an eligible stream, claim the Drop in your Twitch inventory, then come back and click
                    Refresh.
                  </div>
                )}
              </details>
            </section>
          </>
        ) : null}
      </section>
    );
  }
}

export default DropsPage;
